import React, {
	useCallback, useEffect, useMemo, useRef,
} from 'react';
import { Route, Routes, useParams } from 'react-router-dom';
import useStyles from 'isomorphic-style-loader/useStyles';
import { Subject } from 'rxjs';

import classNames from 'classnames';
import { Container, history } from '../../../../symphony';

import { Web } from '../Web/Web';
import { NotWeb } from '../NotWeb/NotWeb';
import { Loader } from '../../components/Loader/Loader';
import { useLeave } from '../../components/HOOKS/useLeave';
import { Header } from '../../components/Header/Header/Header';
import { SlDataService } from '../../services/sl-data.service';
import { ActionType, RequestType } from '../../global-interface';
import { SlActions } from '../../services/ActionsService/sl-actions';
import { FreezeLayer } from '../../components/FreezeLayer/FreezeLayer';
import { FlashMessage } from '../../components/FlashMessage/FlashMessage';
import { LANGUAGE_TYPES, LanguageRoutes } from '../../constants/LanguageTypes';

import s from './PlaygroundContainer.scss';
import { MaterialNotFound } from '../../components/MaterialNotFound/MaterialNotFound';
import { useQuery } from '../../../../shared/public/utilsPublic/utilsPublic';
import { useConfirmLeave } from '../../../../shared/public/SlHooks/useConfirmLeave';
import { CodeLanguages } from '../../../../api/private/global.interface';
import { Footer } from '../../components/Footer/Footer';
import { SlEventsService } from '../../services/Business/sl-events.service';
import { useContainerData } from '../../../../shared/public/SlHooks/useContainerData';
import { lePlaygroundContext } from '../../global-constants';

export interface SlSlPlaygroundContainerProps {
	language?: LanguageRoutes | string | CodeLanguages;
}

export function PlaygroundContainer(): JSX.Element {
	useStyles(s);
	const {
		codeData$: codeData,
		isWeb$: isWeb,
		showConfirmLeaveAlert$: showConfirmLeaveAlert,
		isTIYExpanded,
	} = useContainerData(lePlaygroundContext.value, SlDataService, [
		'codeData$',
		'isWeb$',
		'showConfirmLeaveAlert$',
		'isTIYExpanded',
	]);

	const { language } = useParams();

	const { actions$ } = Container.take(lePlaygroundContext.value, SlActions);
	const eventsService = Container.take(lePlaygroundContext.value, SlEventsService);

	const isTIY = Container.take(
		lePlaygroundContext.value,
		'isTIYWidget',
	);

	const query = useQuery();
	const handleClose = () => {
		const isTryItYourself: boolean = Container.take(
			lePlaygroundContext.value,
			'isTryItYourself',
		);
		if (isTryItYourself) {
			history.push(query.returnurl);
			return;
		}
		const { topLevelDomain$ } = Container.take(
			lePlaygroundContext.value,
			SlDataService,
		);

		actions$.next({
			type: ActionType.MakeFreeze,
			requestType: RequestType.Parallel,
		});

		window.onbeforeunload = null;
		window.location.href = `${topLevelDomain$.value}/codes`;
	};

	const getLanguageType = useMemo(() => (
		LANGUAGE_TYPES.find(
			(item) => item.language === codeData?.language,
		) || LANGUAGE_TYPES.find((item) => item.path === language)
	), [language, codeData?.language]);

	const getContent = useCallback(() => {
		if (codeData) {
			if (isWeb) {
				return <Web />;
			}
			return <NotWeb languageType={getLanguageType} />;
		}
		return null;
	}, [isWeb, codeData]);

	const editorRef1 = useRef(null);
	const editorRef2 = useRef(null);
	const editorRef3 = useRef(null);

	useEffect(() => {
		const unsubscriber = new Subject<void>();
		const courseAlias: string = Container.take(
			lePlaygroundContext.value,
			'courseAlias',
		);

		eventsService.onPlaygroundContainerMount(
			{
				language,
				routeData: {
					returnUrl: query.returnurl,
					courseAlias,
				},
			},
			[editorRef1, editorRef2, editorRef3],
		);

		Container.register(lePlaygroundContext.value, 'onClose', handleClose);

		return () => {
			unsubscriber.next();
			unsubscriber.complete();
		};
	}, []);

	useConfirmLeave(showConfirmLeaveAlert);
	useLeave(
		showConfirmLeaveAlert,
		'Are you sure to leave? Your changes may not be saved.',
	);

	return (
		<div className={classNames('sl-playground-container', {
			tiy: isTIY,
			expanded: isTIYExpanded,
		})}
		>
			<Routes>
				<Route
					path={'/*'}
					element={(
						<>
							<div className="sl-playground-container__header">
								<Header />
							</div>
							<div className="sl-playground-container__content">
								{getContent()}
							</div>
							<div className="sl-playground-container__footer">
								<Footer />
							</div>
						</>
					)}
				/>
				<Route
					path="/not-found-material/*"
					element={<MaterialNotFound />}
				/>
			</Routes>
			<Loader backgroundColor={codeData ? undefined : 'white'} />
			<FlashMessage />
			<FreezeLayer />
		</div>
	);
}
